/**
 * Feedback Screen
 * Dedicated screen for submitting user feedback
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Colors } from '@/constants/colors';
import { FeedbackCategory } from '@/types/feedback.types';
import { submitFeedback } from '@/services/feedback/feedbackService';
import { CategoryTagPicker } from '@/components/feedback/CategoryTagPicker';
import { StarRating } from '@/components/feedback/StarRating';
import { TextInput, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const MAX_LENGTH = 1000;
const SHOW_COUNTER_AT = 900;

export const FeedbackScreen: React.FC = () => {
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [customCategoryText, setCustomCategoryText] = useState('');
  const [urgency, setUrgency] = useState(0);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReset = () => {
    setCategories([]);
    setCustomCategoryText('');
    setUrgency(0);
    setDescription('');
  };

  const handleSubmit = async () => {
    // Validate
    if (categories.length === 0) {
      Alert.alert('Required', 'Please select at least one category');
      return;
    }

    // Validate "other" category requires custom text
    if (categories.includes('other') && !customCategoryText.trim()) {
      Alert.alert('Required', 'Please specify what "Other" category means');
      return;
    }

    if (!description.trim()) {
      Alert.alert('Required', 'Please enter a description');
      return;
    }

    setIsSubmitting(true);

    const result = await submitFeedback({
      categories,
      customCategoryText: customCategoryText.trim() || undefined,
      urgency: urgency > 0 ? urgency : undefined,
      description,
    });

    setIsSubmitting(false);

    if (result.success) {
      Alert.alert(
        '✅ Thank You!',
        "Your feedback helps us improve. We'll review it within 24 hours.",
        [{ text: 'OK', onPress: handleReset }]
      );
    } else {
      Alert.alert('Error', result.error || 'Failed to submit feedback. Please try again.');
    }
  };

  const showCounter = description.length >= SHOW_COUNTER_AT;
  const remaining = MAX_LENGTH - description.length;
  const canSubmit =
    categories.length > 0 &&
    description.trim().length > 0 &&
    !isSubmitting &&
    // Validate "other" requires custom text
    (!categories.includes('other') || customCategoryText.trim().length > 0);

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Send Feedback</Text>
        <Text style={styles.subtitle}>Help us improve the app</Text>
      </View>

      {/* Content */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Category Tag Picker */}
        <CategoryTagPicker
          selectedCategories={categories}
          onCategoriesChange={setCategories}
          customCategoryText={customCategoryText}
          onCustomTextChange={setCustomCategoryText}
          disabled={isSubmitting}
        />

        {/* Urgency Rating */}
        <StarRating rating={urgency} onRatingChange={setUrgency} disabled={isSubmitting} />

        {/* Description */}
        <View style={styles.inputContainer}>
          <Text style={styles.label}>
            Tell us more <Text style={styles.required}>*</Text>
          </Text>
          <TextInput
            style={styles.textArea}
            multiline
            placeholder={
              "Describe the issue or suggestion.\n\nExample: When I tap on AAPL stock, the chart doesn't load."
            }
            placeholderTextColor={Colors.text.tertiary}
            value={description}
            onChangeText={setDescription}
            maxLength={MAX_LENGTH}
            editable={!isSubmitting}
            textAlignVertical="top"
          />
          {showCounter && (
            <Text style={[styles.counter, remaining < 100 && styles.counterWarning]}>
              {remaining} characters remaining
            </Text>
          )}
        </View>

        {/* Info Text */}
        <View style={styles.infoBox}>
          <Ionicons name="information-circle" size={16} color={Colors.text.secondary} />
          <Text style={styles.infoText}>
            Your feedback is collected to help us improve the app. We review all submissions within
            24 hours.
          </Text>
        </View>
      </ScrollView>

      {/* Footer */}
      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.button, styles.clearButton]}
          onPress={handleReset}
          disabled={isSubmitting}
        >
          <Ionicons name="refresh" size={20} color={Colors.text.primary} />
          <Text style={styles.clearButtonText}>Clear</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.button, styles.submitButton, !canSubmit && styles.disabledButton]}
          onPress={handleSubmit}
          disabled={!canSubmit}
        >
          {isSubmitting ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <>
              <Ionicons name="send" size={20} color="#FFFFFF" />
              <Text style={styles.submitButtonText}>Submit Feedback</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  header: {
    padding: 20,
    paddingTop: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 8,
  },
  required: {
    color: Colors.status.error,
  },
  textArea: {
    backgroundColor: Colors.background.tertiary,
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    color: Colors.text.primary,
    minHeight: 120,
    maxHeight: 200,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  counter: {
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'right',
    marginTop: 4,
  },
  counterWarning: {
    color: Colors.status.warning,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: Colors.background.tertiary,
    padding: 12,
    borderRadius: 8,
    gap: 8,
    marginTop: 8,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    color: Colors.text.secondary,
    lineHeight: 16,
  },
  footer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
  },
  clearButton: {
    backgroundColor: Colors.background.tertiary,
  },
  clearButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  submitButton: {
    backgroundColor: Colors.accent.primary,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  disabledButton: {
    opacity: 0.5,
  },
});
